/**
 * Member timeline API wrapper (DNKADM-0429, contract DNKAPI-0614).
 *
 * `GET /v2/admin/members/{contactId}/timeline` is ONE newest-first feed per member that
 * merges the reservation lifecycle, recorded refusals, money rows and credit-ledger rows.
 * The server owns the order (`occurredAt DESC` with a total tie-break), so the UI never
 * re-sorts it.
 *
 * Paging is a CURSOR, not a page index: `nextCursor` is opaque (never parsed or built
 * here), `null` on the last page, and passed back verbatim as `cursor`. There is NO total
 * count (a three-store merge has none), so nothing here exposes one.
 *
 * Filters: `category` (BOOKING, MONEY, CREDITS, REFUSED; sent comma-separated, which the
 * contract accepts), and `from`/`to` (`yyyy-MM-dd`, tenant-local, inclusive). Each is sent
 * ONLY when set; no category means all four.
 *
 * Bearer-only `/v2/admin/**` surface through `request()`: bearer token, NO CSRF, NO
 * cookies, the bounded 401→refresh→retry.
 *
 * DEFENSIVE PARSE: every key is optional on our side even though the contract says every
 * key is present. A row with no `id` is dropped (the id is the dedupe key across pages);
 * `kind`/`category` stay raw strings so a kind the API adds later still renders. `details`
 * is parsed only for the cancel credit outcome (DNKADM-0449, contract DNKAPI-0655), a
 * declined payment's `declineReason` (DNKADM-0466, contract DNKAPI-0676), and a credit
 * item's `lotCount` / `lotExpiresAt` (DNKADM-0473, contract DNKAPI-0687); the server
 * already folds the 0613 check-in choice into `title`.
 */
import { request } from './client';
import { parseDeclineReason, type DeclineReason } from '@/utils/declineReason';

/** The four filterable categories, in the order the UI offers them. */
export const TIMELINE_CATEGORIES = ['BOOKING', 'MONEY', 'CREDITS', 'REFUSED'] as const;
export type TimelineCategory = (typeof TIMELINE_CATEGORIES)[number];

export type TimelineActorType = 'ADMIN' | 'MEMBER' | 'GUEST' | 'SYSTEM';

export interface TimelineActor {
  /** `null` for a type outside the contract's four. */
  type: TimelineActorType | null;
  name: string | null;
  email: string | null;
}

export interface TimelineLinks {
  reservationUuid: string | null;
  paymentId: number | null;
  /**
   * The member's order this item belongs to (DNKAPI-0687): set on COMP, ORDER_VOID and a
   * payment that settles an order of this member. Read with
   * `GET /v2/admin/members/{contactId}/orders/{orderId}`.
   */
  orderId: number | null;
  /** The order's receipt number; set exactly when `orderId` is. */
  orderNumber: string | null;
}

/** Why a cancel kept credit; an unknown reason string parses to `null` (no reason clause). */
export type CancelCreditReason = 'INSIDE_POLICY_WINDOW' | 'STAFF_NONE' | 'REFUND_LOCKED';

/**
 * A reservation_cancel's recorded credit outcome (DNKAPI-0655). Counts are DECIMAL(8,2)
 * server-side, so they are numbers (1 or 0.5), `null` when not a number.
 */
export interface CancelCreditOutcome {
  creditsKept: number | null;
  creditsReturned: number | null;
  bucketName: string | null;
  reason: CancelCreditReason | null;
}

export interface TimelineItem {
  /** `audit:<id>` / `payment:<id>` / `credit:<id>`; unique across the timeline. */
  id: string;
  occurredAt: string | null;
  /** Raw kind; unknown values are kept, never coerced. */
  kind: string | null;
  category: string | null;
  title: string | null;
  /** UNSIGNED minor units; `kind` gives the direction. */
  amountMinor: number | null;
  currency: string | null;
  /** Signed credit delta (CREDITS rows only). */
  creditChange: number | null;
  /** The bucket's balance after this row (CREDITS rows only). */
  balance: number | null;
  /** `null` when the API cannot say who acted. */
  actor: TimelineActor | null;
  links: TimelineLinks;
  /** `null` when `details` carries no recorded outcome (card, comp, free, historic). */
  cancelOutcome: CancelCreditOutcome | null;
  /**
   * A declined card payment's reason (`details.declineReason`, DNKAPI-0676), or null when
   * unknown, not a payment, or a value this build does not know.
   */
  declineReason?: DeclineReason | null;
  /**
   * CREDITS items: how many ledger rows this item stands for (DNKAPI-0687). Greater than 1
   * for a booking debit or cancel refund that spanned several lots, folded into ONE item
   * whose `creditChange` is the sum. `null` when absent.
   */
  lotCount?: number | null;
  /**
   * A CREDIT_EXPIRY item's own lot boundary (DNKAPI-0687), ISO instant: when the lot
   * really expired. `occurredAt` is when the row was written, which can be later. `null`
   * when absent.
   */
  lotExpiresAt?: string | null;
}

export interface TimelinePage {
  items: TimelineItem[];
  /** Opaque; `null` on the last page. */
  nextCursor: string | null;
}

export interface TimelineParams {
  /** Empty or omitted means every category. */
  categories?: readonly TimelineCategory[];
  /** `yyyy-MM-dd`, tenant-local, inclusive. */
  from?: string;
  /** `yyyy-MM-dd`, tenant-local, inclusive. */
  to?: string;
  cursor?: string;
  size?: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function parseActorType(raw: unknown): TimelineActorType | null {
  const text = asText(raw)?.toUpperCase() ?? null;
  return text === 'ADMIN' || text === 'MEMBER' || text === 'GUEST' || text === 'SYSTEM'
    ? text
    : null;
}

function parseActor(raw: unknown): TimelineActor | null {
  if (!isObject(raw)) return null;
  return {
    type: parseActorType(raw['type']),
    name: asText(raw['name']),
    email: asText(raw['email']),
  };
}

function parseLinks(raw: unknown): TimelineLinks {
  const o = isObject(raw) ? raw : {};
  return {
    reservationUuid: asText(o['reservationUuid']),
    paymentId: asNumber(o['paymentId']),
    orderId: asNumber(o['orderId']),
    orderNumber: asText(o['orderNumber']),
  };
}

function parseCancelOutcome(raw: unknown): CancelCreditOutcome | null {
  if (!isObject(raw)) return null;
  const reasonText = asText(raw['reason']);
  const reason: CancelCreditReason | null =
    reasonText === 'INSIDE_POLICY_WINDOW' ||
    reasonText === 'STAFF_NONE' ||
    reasonText === 'REFUND_LOCKED'
      ? reasonText
      : null;
  const outcome: CancelCreditOutcome = {
    creditsKept: asNumber(raw['creditsKept']),
    creditsReturned: asNumber(raw['creditsReturned']),
    bucketName: asText(raw['bucketName']),
    reason,
  };
  return outcome.creditsKept === null && outcome.creditsReturned === null ? null : outcome;
}

function parseItem(raw: unknown): TimelineItem | null {
  if (!isObject(raw)) return null;
  const id = asText(raw['id']);
  if (id === null) return null;
  const details = isObject(raw['details']) ? raw['details'] : null;
  return {
    id,
    occurredAt: asText(raw['occurredAt']),
    kind: asText(raw['kind']),
    category: asText(raw['category']),
    title: asText(raw['title']),
    amountMinor: asNumber(raw['amountMinor']),
    currency: asText(raw['currency']),
    creditChange: asNumber(raw['creditChange']),
    balance: asNumber(raw['balance']),
    actor: parseActor(raw['actor']),
    links: parseLinks(raw['links']),
    cancelOutcome: parseCancelOutcome(raw['details']),
    declineReason: details !== null ? parseDeclineReason(details['declineReason']) : null,
    lotCount: details !== null ? asNumber(details['lotCount']) : null,
    lotExpiresAt: details !== null ? asText(details['lotExpiresAt']) : null,
  };
}

function parsePage(body: unknown): TimelinePage {
  const o = isObject(body) ? body : {};
  const content = Array.isArray(o['content']) ? o['content'] : [];
  return {
    items: content.map(parseItem).filter((item): item is TimelineItem => item !== null),
    nextCursor: asText(o['nextCursor']),
  };
}

/** `GET /v2/admin/members/{contactId}/timeline`: one page, in the server's order. */
export async function getMemberTimeline(
  contactId: string,
  params: TimelineParams = {},
): Promise<TimelinePage> {
  const categories = params.categories ?? [];
  const body = await request<unknown>(
    'GET',
    `/v2/admin/members/${encodeURIComponent(contactId)}/timeline`,
    {
      query: {
        ...(categories.length > 0 ? { category: categories.join(',') } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
        ...(params.size !== undefined ? { size: params.size } : {}),
      },
    },
  );
  return parsePage(body);
}
